import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import ButtonGroup from "@/components/ui/ButtonGroup";
import CountBox from "@/components/ui/CountBox";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { useState, useMemo, useEffect } from "react";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import Table, { TableColumn } from "@/components/ui/Table";
import { useHardDeleteSaleSlip, useRestoreSaleSlip } from "@/hooks/performance/useSaleSlip";
import { toast } from "sonner";
import { useUserInfo } from "@/hooks/member/useMember";
import { useOrderSaleSlipReport } from "@/hooks/dispatch/dp_ordeer_slip/useOrderSlip";
import { OrderSaleSlipReportRow, SaleSlipReportSearchDto } from "@/interface/dispatch/dp_order_slip";

export default function SalesDel() {
  // 사용자 정보 가져오기
  const { data: userInfo } = useUserInfo();

  // 하드 삭제 mutation (영구 삭제)
  const hardDeleteSaleSlipMutation = useHardDeleteSaleSlip();
  // 복구 mutation
  const restoreSaleSlipMutation = useRestoreSaleSlip();

  const [searchValue, setSearchValue] = useState("");
  const [selectedValue, setSelectedValue] = useState("all");

  // 검색 필드 선택 및 검색어
  const [searchField, setSearchField] = useState("custName"); // 기본값: 거래처
  const [searchKeyword, setSearchKeyword] = useState(""); // 검색어 입력값
  const [searchField2, setSearchField2] = useState("carNumber"); // 두 번째 검색 필드 기본값: 차량번호
  const [searchKeyword2, setSearchKeyword2] = useState(""); // 두 번째 검색어 입력값

  // 실제 적용된 검색 조건 (조회 버튼 클릭 시 적용)
  const [appliedSearchDto, setAppliedSearchDto] = useState<SaleSlipReportSearchDto>({ delYn: "Y" });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [dateBtnValue, setDateBtnValue] = useState("today");
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<OrderSaleSlipReportRow[]>([]);
  const [tableVer, setTableVer] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // 이번 달 1일 ~ 오늘 날짜를 기본값으로 설정
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // 로컬 날짜 문자열로 변환 (UTC 변환 방지)
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const firstDayStr = `${firstDayOfMonth.getFullYear()}-${String(firstDayOfMonth.getMonth() + 1).padStart(2, "0")}-01`;

  const [startDate, setStartDate] = useState(firstDayStr);
  const [endDate, setEndDate] = useState(todayStr);

  // 매출 전표 리포트 조회 (delYn: "Y"로 조회)
  const { data: orderSaleSlipReportData, isLoading } = useOrderSaleSlipReport({
    page: currentPage,
    size: 20,
    dateFrom: startDate,
    dateTo: endDate,
    searchDto: {
      ...appliedSearchDto,
      delYn: "Y", // 삭제된 항목만 조회
    },
  });

  // 조회 조건 변경 시 페이지네이션 상태 초기화
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [startDate, endDate, appliedSearchDto]);

  // 새 페이지 데이터 반영
  useEffect(() => {
    if (orderSaleSlipReportData && orderSaleSlipReportData.content) {
      if (currentPage === 0) {
        setAllData(orderSaleSlipReportData.content);
      } else if (orderSaleSlipReportData.content.length > 0) {
        setAllData(prev => [...prev, ...orderSaleSlipReportData.content!]);
      }
      setLoadingMore(false);
    }
  }, [orderSaleSlipReportData, currentPage]);

  const totalElements = orderSaleSlipReportData?.totalElements || 0;

  const hasMore = useMemo(() => {
    if (!orderSaleSlipReportData) return false;
    return !orderSaleSlipReportData.last && allData.length < totalElements;
  }, [orderSaleSlipReportData?.last, allData.length, totalElements]);

  const companyOptions = [
    { label: "회사1", value: "1" },
    { label: "회사2", value: "2" },
    { label: "회사3", value: "3" },
  ];

  // 검색 필드 옵션
  const searchFieldOptions = [
    { label: "거래처", value: "custName" },
    { label: "차량번호", value: "carNumber" },
    { label: "기사명", value: "driverName" },
    { label: "전화번호", value: "driverTel" },
    { label: "상차지", value: "inLocation" },
    { label: "하차지", value: "outLocation" },
    { label: "하차지역1", value: "outArea1" },
    { label: "하차지역2", value: "outArea2" },
    { label: "배차사원", value: "dispatchStaffName" },
    { label: "품목명", value: "itemName" },
  ];

  const handleReset = () => {
    setSearchValue("");
    setSearchField("custName");
    setSearchKeyword("");
    setSearchField2("carNumber");
    setSearchKeyword2("");
    setAppliedSearchDto({});
    // 이번 달 1일 ~ 오늘 날짜로 초기화
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const firstDayStr = `${firstDayOfMonth.getFullYear()}-${String(firstDayOfMonth.getMonth() + 1).padStart(2, "0")}-01`;
    setStartDate(firstDayStr);
    setEndDate(todayStr);
    setSelectedValue("all");
    setDateBtnValue("today");
    // 페이지 초기화
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  };

  const handleSearch = () => {
    const newSearchDto: SaleSlipReportSearchDto = {};
    if (searchKeyword.trim()) {
      (newSearchDto as Record<string, string | boolean | undefined>)[searchField] = searchKeyword.trim();
    }
    if (searchKeyword2.trim()) {
      (newSearchDto as Record<string, string | boolean | undefined>)[searchField2] = searchKeyword2.trim();
    }
    setAppliedSearchDto(newSearchDto);
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  };

  // 자료복구 핸들러
  const handleRestore = () => {
    if (selectedRows.length === 0) {
      toast.warning("복구할 항목을 선택해주세요.");
      return;
    }

    // 선택된 행들의 saleSlipCode와 orderSlipCode 수집
    const selectedItems = selectedRows.map(index => allData[index]).filter(item => item);

    const saleSlipCodesToRestore = selectedItems
      .map(item => item?.saleSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    const orderSlipCodesToRestore = selectedItems
      .map(item => item?.orderSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    if (saleSlipCodesToRestore.length === 0 && orderSlipCodesToRestore.length === 0) {
      toast.error("복구할 수 있는 항목이 없습니다.");
      return;
    }

    // 확인 대화상자
    const totalCount = saleSlipCodesToRestore.length + orderSlipCodesToRestore.length;
    if (window.confirm(`선택한 ${totalCount}개의 매출 전표를 복구하시겠습니까?`)) {
      const delId = userInfo?.name || "";
      if (!delId) {
        toast.error("사용자 정보를 가져올 수 없습니다.");
        return;
      }

      restoreSaleSlipMutation.mutate(
        { saleSlipCodes: saleSlipCodesToRestore, orderSlipCodes: orderSlipCodesToRestore, delId },
        {
          onSuccess: () => {
            toast.success(`${totalCount}개의 매출 전표가 복구되었습니다.`);
            handleReset();
          },
          onError: (error: any) => {
            console.error("복구 실패:", error);
            toast.error(error?.response?.data?.message || "매출 전표 복구에 실패했습니다.");
          },
        }
      );
    }
  };

  // 자료삭제 핸들러 (완전 삭제 - 이미 delYn이 'Y'인 항목을 영구 삭제)
  const handlePermanentDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 항목을 선택해주세요.");
      return;
    }

    // 선택된 행들의 saleSlipCode와 orderSlipCode 수집
    const selectedItems = selectedRows.map(index => allData[index]).filter(item => item);

    const saleSlipCodesToDelete = selectedItems
      .map(item => item?.saleSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    const orderSlipCodesToDelete = selectedItems
      .map(item => item?.orderSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    if (saleSlipCodesToDelete.length === 0 && orderSlipCodesToDelete.length === 0) {
      toast.error("삭제할 수 있는 항목이 없습니다.");
      return;
    }

    // 확인 대화상자
    const totalCount = saleSlipCodesToDelete.length + orderSlipCodesToDelete.length;
    if (window.confirm(`선택한 ${totalCount}개의 매출 전표를 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      hardDeleteSaleSlipMutation.mutate(
        { saleSlipCodes: saleSlipCodesToDelete, orderSlipCodes: orderSlipCodesToDelete },
        {
          onSuccess: () => {
            toast.success(`${totalCount}개의 매출 전표가 영구 삭제되었습니다.`);
            handleReset();
          },
          onError: (error: any) => {
            console.error("삭제 실패:", error);
            toast.error(error?.response?.data?.message || "매출 전표 삭제에 실패했습니다.");
          },
        }
      );
    }
  };

  // 무한 스크롤 설정
  const infiniteScrollPage = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore && !loadingMore) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  // 테이블 컬럼 정의 (OrderSaleSlipReportRow 필드에 맞게 수정)
  const columns: TableColumn[] = [
    {
      key: "baseDate",
      header: "매출일자\n(주문일자)",
      sticky: "left",
      render: (value: string, row: OrderSaleSlipReportRow) => {
        // 날짜 포맷팅 헬퍼 함수
        const formatDate = (date: string | undefined): string => {
          if (!date) return "";
          return String(date).split("T")[0];
        };

        // 우선순위: saleDate > orderDate > baseDate > value
        if (row?.saleDate) return formatDate(row.saleDate);
        if (row?.orderDate) return `(${formatDate(row.orderDate)})`;
        if (row?.baseDate) return formatDate(row.baseDate);
        if (value) return formatDate(value);
        return "";
      },
    },
    { key: "custName", header: "매출처", bodyAlign: "left", sticky: "left" },
    { key: "itemName", header: "품목명", bodyAlign: "left", sticky: "left" },

    { key: "carNumber", header: "차량번호", bodyAlign: "left", group: "차량정보" },
    { key: "driverName", header: "기사명", group: "차량정보" },
    { key: "driverTel", header: "전화번호", group: "차량정보" },

    { key: "inBranch1", header: "대리점1", bodyAlign: "left", group: "상차정보" },
    { key: "inLocation1", header: "상차지1", bodyAlign: "left", group: "상차정보" },
    {
      key: "inQty1",
      header: "수량1",
      bodyAlign: "right",
      group: "상차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    { key: "outLocation1", header: "하차지1", bodyAlign: "left", group: "하차정보" },
    { key: "outArea1", header: "지역1", bodyAlign: "left", group: "하차정보" },
    {
      key: "outQty1",
      header: "수량1",
      bodyAlign: "right",
      group: "하차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    { key: "outLocation2", header: "하차지2", bodyAlign: "left", group: "하차정보" },
    { key: "outArea2", header: "지역2", bodyAlign: "left", group: "하차정보" },
    {
      key: "outQty2",
      header: "수량2",
      bodyAlign: "right",
      group: "하차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    { key: "outLocation3", header: "하차지3", bodyAlign: "left", group: "하차정보" },
    { key: "outArea3", header: "지역3", bodyAlign: "left", group: "하차정보" },
    {
      key: "outQty3",
      header: "수량3",
      bodyAlign: "right",
      group: "하차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    {
      key: "saleUnitPrice",
      header: "단가",
      bodyAlign: "right",
      group: "매출정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "saleAmount",
      header: "매출액",
      bodyAlign: "right",
      group: "매출정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "saleTotal",
      header: "합계",
      bodyAlign: "right",
      group: "매출정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    {
      key: "buyUnitPrice",
      header: "단가",
      bodyAlign: "right",
      group: "매입정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "buyAmount",
      header: "매입액",
      bodyAlign: "right",
      group: "매입정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "buyTotal",
      header: "합계",
      bodyAlign: "right",
      group: "매입정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    {
      key: "profitAmt",
      header: "금액",
      bodyAlign: "right",
      group: "운송마진",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "profitRate",
      header: "비율",
      bodyAlign: "right",
      group: "운송마진",
      render: (value: number) => (value ? `${value.toFixed(2)}%` : ""),
    },

    { key: "remark", header: "비고", bodyAlign: "left" },
  ];

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex items-end mb-3">
          <SelectBox
            label="회사"
            className="w-50"
            value={searchValue}
            onChange={e => setSearchValue(e)}
            options={companyOptions}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField)?.label || "검색필드"}
            className="min-w-24"
            value={searchField}
            onChange={e => setSearchField(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField2)?.label || "검색필드"}
            className="min-w-24"
            value={searchField2}
            onChange={e => setSearchField2(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword2}
            onChange={e => setSearchKeyword2(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <DateBox
            label="매출일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />
        </div>
        <div className="flex items-end">
          <ButtonGroup
            label="표시선택"
            options={[
              { value: "all", label: "전체" },
              { value: "option1", label: "마감" },
              { value: "option2", label: "진행중" },
            ]}
            value={selectedValue}
            onChange={setSelectedValue}
          />
          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={totalElements} />
          <div className="flex gap-2">
            <PermissionButton permission="canUpdate" variant="primary" onClick={handleRestore}>
              자료복구
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handlePermanentDelete}>
              삭제
            </PermissionButton>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`sales-del-table-v${tableVer}`}
            resizable
            {...infiniteScrollPage.tableProps}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            selectedRows={selectedRows}
            stickyCheckbox
            onRowSelect={setSelectedRows}
            maxHeight="100%"
            loadingMore={loadingMore}
            loading={isLoading && currentPage === 0}
            emptyMessage="데이터가 없습니다."
          />
        </div>
      </main>
    </div>
  );
}
